import React, { useState, useEffect } from 'react';
import { FiBook, FiTrash2, FiEye, FiFilter, FiEdit, FiArrowUp, FiArrowDown } from 'react-icons/fi';
import { adminAPI, studentAPI } from '../../../services/api';
import { useNavigate } from 'react-router-dom';
import { useSidebar } from '../../../contexts/SidebarContext';
import './ViewLessonsPage.css';

function ViewLessonsPage() {
    const navigate = useNavigate();
    const [lessons, setLessons] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const { sidebarOpen, setSidebarOpen } = useSidebar();
    
    const [filters, setFilters] = useState({
        category: '',
        levelId: '',
        areaId: ''
    });
    
    const [areas, setAreas] = useState([]);
    const [levels, setLevels] = useState([]);
    const [areasLoading, setAreasLoading] = useState(true);
    const [levelsLoading, setLevelsLoading] = useState(true);
    
    // Sidebar states (как у студента)
    const [sidebarCategory, setSidebarCategory] = useState('grammar');
    const [sidebarAreas, setSidebarAreas] = useState([]);
    const [sidebarSelectedArea, setSidebarSelectedArea] = useState(null);
    const [sidebarLessons, setSidebarLessons] = useState([]);
    const [expandedSidebarLessons, setExpandedSidebarLessons] = useState({});
    const [sortMode, setSortMode] = useState(false);
    const [draggedIndex, setDraggedIndex] = useState(null);
    const [reordering, setReordering] = useState(false);

    useEffect(() => {
        loadAreas();
        loadLevels();
        loadLessons();
        loadSidebarData();
    }, []);
    
    useEffect(() => {
        loadSidebarData();
    }, [sidebarCategory]);

    const loadAreas = async () => {
        try {
            setAreasLoading(true);
            const response = await adminAPI.getAreas();
            setAreas(response.data.areas || []);
        } catch (err) {
            console.error('Ошибка загрузки областей:', err);
            setAreas([]);
        } finally {
            setAreasLoading(false);
        }
    };

    const loadLevels = async () => {
        try {
            setLevelsLoading(true);
            const response = await adminAPI.getLevels();
            // API возвращает объект вида { levels: [...] }
            setLevels(response.data?.levels || []);
        } catch (err) {
            console.error('Ошибка загрузки уровней:', err);
            setLevels([]);
        } finally {
            setLevelsLoading(false);
        }
    };

    const loadLessons = async () => {
        try {
            setLoading(true);
            setError('');
            const params = {};
            if (filters.category) params.category = filters.category;
            if (filters.levelId) params.levelId = filters.levelId;
            if (filters.areaId) params.areaId = filters.areaId;
            
            const response = await adminAPI.getLessonsBlocks(params);
            setLessons(Array.isArray(response.data) ? response.data : []);
        } catch (err) {
            console.error('Ошибка загрузки уроков:', err);
            setError('Ошибка при загрузке уроков');
            setLessons([]);
        } finally {
            setLoading(false);
        }
    };
    
    const loadSidebarData = async () => {
        try {
            const areasRes = await adminAPI.getAreas({ category: sidebarCategory });
            const areas = areasRes.data.areas || [];
            setSidebarAreas(areas);
        } catch (error) {
            console.error('Ошибка загрузки областей sidebar:', error);
        }
    };
    
    const toggleSidebarLessonExpand = async (lessonIdToToggle) => {
        const isExpanded = expandedSidebarLessons[lessonIdToToggle];
        if (!isExpanded) {
            try {
                const response = await studentAPI.getLesson(lessonIdToToggle);
                const lessonData = response.data;
                setExpandedSidebarLessons(prev => ({
                    ...prev,
                    [lessonIdToToggle]: lessonData.blocks || []
                }));
            } catch (error) {
                console.error('Ошибка загрузки блоков урока:', error);
            }
        } else {
            setExpandedSidebarLessons(prev => ({
                ...prev,
                [lessonIdToToggle]: false
            }));
        }
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const applyFilters = () => {
        loadLessons();
    };

    const resetFilters = () => {
        setFilters({
            category: '',
            levelId: '',
            areaId: ''
        });
        setTimeout(() => loadLessons(), 100);
    };

    const handleViewDetails = (lesson) => {
        const num = lesson.lesson_number ?? lesson.id;
        const params = lesson.level_id != null ? `?levelId=${lesson.level_id}` : '';
        navigate(`/admin/lessons/${num}/view${params}`);
    };

    const handleDeleteLesson = async (lessonId, lessonTitle) => {
        if (!window.confirm(`Вы уверены, что хотите удалить урок "${lessonTitle}"? Это действие нельзя отменить.`)) {
            return;
        }

        try {
            await adminAPI.deleteLessonBlocks(lessonId);
            alert('Урок успешно удален');
            loadLessons();
        } catch (err) {
            console.error('Ошибка удаления урока:', err);
            alert('Ошибка при удалении урока');
        }
    };

    const persistLessonsOrder = async (orderedLessons) => {
        // Присваиваем последовательные значения, чтобы исключить дубли sort_order.
        await Promise.all(
            orderedLessons.map((lesson, index) =>
                adminAPI.updateLessonSortOrder(lesson.id, index)
            )
        );
    };

    const reorderByIndexes = async (fromIndex, toIndex) => {
        if (fromIndex === toIndex || fromIndex == null || toIndex == null) return;
        try {
            setReordering(true);
            const reordered = [...lessons];
            const [movedLesson] = reordered.splice(fromIndex, 1);
            reordered.splice(toIndex, 0, movedLesson);
            setLessons(reordered);
            await persistLessonsOrder(reordered);
            await loadLessons();
        } catch (err) {
            console.error('Ошибка перемещения урока:', err);
            alert('Ошибка при перемещении урока');
        } finally {
            setReordering(false);
            setDraggedIndex(null);
        }
    };

    const handleMoveLessonUp = async (currentIndex) => {
        if (currentIndex === 0) return;
        await reorderByIndexes(currentIndex, currentIndex - 1);
    };

    const handleMoveLessonDown = async (currentIndex) => {
        if (currentIndex === lessons.length - 1) return;
        await reorderByIndexes(currentIndex, currentIndex + 1);
    };

    if (loading) {
        return <div className="view-lessons-loading">Загрузка уроков...</div>;
    }

    return (
        <div className="view-lessons-container" style={{ display: 'flex', height: 'calc(100vh - 60px)', overflow: 'hidden' }}>
            {/* Overlay для мобильных */}
            {sidebarOpen && (
                <div 
                    className="sidebar-overlay"
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* Боковое меню как у студента */}
            <aside className={`lessons-sidebar ${sidebarOpen ? 'open' : ''}`} style={{
                width: '280px',
                background: '#f8f9fa',
                borderRight: '1px solid #dee2e6',
                overflowY: 'auto',
                height: '100%'
            }}>
                <div style={{
                    padding: '20px',
                    borderBottom: '1px solid #dee2e6',
                    background: 'white'
                }}>
                    <h3 style={{ 
                        margin: '0 0 15px 0', 
                        fontSize: '16px',
                        color: '#333',
                        fontWeight: '600'
                    }}>
                        Категория
                    </h3>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <button
                            onClick={() => setSidebarCategory('grammar')}
                            style={{
                                padding: '12px 16px',
                                background: sidebarCategory === 'grammar' ? '#e7f3ff' : 'white',
                                border: `2px solid ${sidebarCategory === 'grammar' ? '#4a90e2' : '#e0e0e0'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: sidebarCategory === 'grammar' ? '600' : '500',
                                color: '#333',
                                textAlign: 'left'
                            }}
                        >
                            Грамматика
                        </button>
                        <button
                            onClick={() => setSidebarCategory('vocabulary')}
                            style={{
                                padding: '12px 16px',
                                background: sidebarCategory === 'vocabulary' ? '#e7f3ff' : 'white',
                                border: `2px solid ${sidebarCategory === 'vocabulary' ? '#4a90e2' : '#e0e0e0'}`,
                                borderRadius: '8px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                fontWeight: sidebarCategory === 'vocabulary' ? '600' : '500',
                                color: '#333',
                                textAlign: 'left'
                            }}
                        >
                            Лексика
                        </button>
                    </div>
                </div>
                
                <div style={{ padding: '15px' }}>
                    <h4 style={{ 
                        margin: '0 0 12px 0', 
                        fontSize: '14px', 
                        color: '#666',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px'
                    }}>
                        Области обучения
                    </h4>
                    {sidebarAreas.length === 0 ? (
                        <p style={{ fontSize: '13px', color: '#999' }}>Нет областей</p>
                    ) : (
                        sidebarAreas.map(area => (
                            <div
                                key={area.id}
                                onClick={() => {
                                    if (sidebarSelectedArea === area.id) {
                                        setSidebarSelectedArea(null);
                                        setSidebarLessons([]);
                                    } else {
                                        setSidebarSelectedArea(area.id);
                                        studentAPI.getLevels(area.id).then(res => {
                                            const levels = res.data.levels || [];
                                            Promise.all(levels.map(level =>
                                                studentAPI.getLessons(level.id).then(r => r.data.lessons || [])
                                            )).then(arrays => {
                                                setSidebarLessons(arrays.flat().filter(l => l.category === sidebarCategory));
                                            });
                                        });
                                    }
                                }}
                                style={{
                                    padding: '12px 16px',
                                    margin: '8px 0',
                                    background: sidebarSelectedArea === area.id ? '#e7f3ff' : 'white',
                                    border: `2px solid ${sidebarSelectedArea === area.id ? '#4a90e2' : '#e0e0e0'}`,
                                    borderRadius: '8px',
                                    cursor: 'pointer',
                                    fontSize: '14px',
                                    fontWeight: sidebarSelectedArea === area.id ? '600' : '500',
                                    color: '#333'
                                }}
                            >
                                {area.name || area.title}
                            </div>
                        ))
                    )}
                </div>
                
                {/* Список уроков в sidebar */}
                {sidebarLessons.length > 0 && (
                    <div style={{ padding: '15px', borderTop: '1px solid #dee2e6' }}>
                        <h4 style={{ 
                            margin: '0 0 12px 0', 
                            fontSize: '14px', 
                            color: '#666',
                            fontWeight: '600',
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px'
                        }}>
                            Уроки
                        </h4>
                        {sidebarLessons.map(lesson => {
                            const isExpanded = expandedSidebarLessons[lesson.id];
                            const hasLessonBlocks = Array.isArray(isExpanded) && isExpanded.length > 0;
                            
                            return (
                                <div key={lesson.id} style={{ marginBottom: '8px' }}>
                                    <div style={{
                                        padding: '10px 12px',
                                        margin: '4px 0',
                                        background: 'white',
                                        border: '1px solid #e0e0e0',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '500',
                                        color: '#333',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        gap: '8px'
                                    }}>
                                        <span
                                            onClick={() => handleViewDetails(lesson)}
                                            style={{ flex: 1 }}
                                        >
                                            {lesson.title}
                                        </span>
                                        <button
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                toggleSidebarLessonExpand(lesson.id);
                                            }}
                                            style={{
                                                background: 'transparent',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: '14px',
                                                color: '#4a90e2',
                                                padding: '0 5px'
                                            }}
                                        >
                                            {isExpanded ? '▲' : '▼'}
                                        </button>
                                    </div>
                                    
                                    {/* Блоки урока */}
                                    {isExpanded && Array.isArray(isExpanded) && isExpanded.length > 0 && (
                                        <div style={{ marginLeft: '15px', marginTop: '5px' }}>
                                            {isExpanded.map((block, blockIdx) => (
                                                <div
                                                    key={blockIdx}
                                                    onClick={() => {
                                                        const num = lesson.lesson_number ?? lesson.id;
                                                        const params = new URLSearchParams();
                                                        if (lesson.level_id != null) params.set('levelId', String(lesson.level_id));
                                                        params.set('block', String(blockIdx));
                                                        navigate(`/admin/lessons/${num}/view?${params.toString()}`);
                                                    }}
                                                    style={{
                                                        padding: '8px 10px',
                                                        margin: '4px 0',
                                                        background: '#f8f9fa',
                                                        border: '1px solid #e0e0e0',
                                                        borderRadius: '4px',
                                                        cursor: 'pointer',
                                                        fontSize: '12px',
                                                        color: '#666',
                                                        transition: 'all 0.2s'
                                                    }}
                                                    onMouseEnter={(e) => {
                                                        e.currentTarget.style.background = '#e3f2fd';
                                                        e.currentTarget.style.borderColor = '#4a90e2';
                                                        e.currentTarget.style.color = '#4a90e2';
                                                    }}
                                                    onMouseLeave={(e) => {
                                                        e.currentTarget.style.background = '#f8f9fa';
                                                        e.currentTarget.style.borderColor = '#e0e0e0';
                                                        e.currentTarget.style.color = '#666';
                                                    }}
                                                >
                                                    {block.title}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </aside>

            {/* Основной контент */}
            <div className="view-lessons-page" style={{ flex: 1, overflowY: 'auto' }}>
            <div className="view-lessons-header">
                <div className="header-top">
                    <FiBook className="header-icon" />
                    <h1>Все уроки</h1>
                </div>
                
                <div className="filters-section">
                    <div className="filters-icon">
                        <FiFilter />
                        <span>Фильтры</span>
                    </div>
                    
                    <div className="filters-row">
                        <select 
                            value={filters.category} 
                            onChange={(e) => handleFilterChange('category', e.target.value)}
                        >
                            <option value="">Все категории</option>
                            <option value="grammar">Грамматика</option>
                            <option value="vocabulary">Лексика</option>
                        </select>

                        <select 
                            value={filters.areaId} 
                            onChange={(e) => handleFilterChange('areaId', e.target.value)}
                        >
                            <option value="">Все области</option>
                            {areas.map(area => (
                                <option key={area.id} value={area.id}>{area.name}</option>
                            ))}
                        </select>

                        <select 
                            value={filters.levelId} 
                            onChange={(e) => handleFilterChange('levelId', e.target.value)}
                        >
                            <option value="">Все уровни</option>
                            {levels.map(level => (
                                <option key={level.id} value={level.id}>{level.name}</option>
                            ))}
                        </select>

                        <button onClick={applyFilters} className="btn-apply-filters">
                            Применить
                        </button>
                        <button onClick={resetFilters} className="btn-reset-filters">
                            Сбросить
                        </button>
                        <button
                            onClick={() => setSortMode((prev) => !prev)}
                            className="btn-sort-mode"
                            title="Включить удобную сортировку в одну колонку"
                        >
                            {sortMode ? 'Выключить сортировку' : 'Режим сортировки'}
                        </button>
                    </div>
                </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <div className={`lessons-list ${sortMode ? 'sorting-mode' : ''}`}>
                {lessons.length === 0 ? (
                    <div className="no-lessons">
                        <FiBook size={48} />
                        <p>Уроков пока нет</p>
                    </div>
                ) : (
                    lessons.map((lesson, index) => (
                        <div
                            key={lesson.id}
                            className={`lesson-card ${sortMode ? 'sortable-card' : ''} ${draggedIndex === index ? 'dragging' : ''}`}
                            draggable={sortMode && !reordering}
                            onDragStart={() => setDraggedIndex(index)}
                            onDragOver={(e) => {
                                if (!sortMode) return;
                                e.preventDefault();
                            }}
                            onDrop={async (e) => {
                                if (!sortMode) return;
                                e.preventDefault();
                                await reorderByIndexes(draggedIndex, index);
                            }}
                            onDragEnd={() => setDraggedIndex(null)}
                        >
                            <div className="lesson-card-header">
                                <div className="lesson-category">
                                    {lesson.category === 'grammar' ? 'Грамматика' : 'Лексика'}
                                </div>
                                <div className="lesson-date">
                                    {new Date(lesson.created_at).toLocaleDateString('ru-RU')}
                                </div>
                            </div>
                            {sortMode && (
                                <div className="sort-hint">
                                    {index + 1}. Перетащите карточку на нужное место
                                </div>
                            )}
                            
                            <h3 className="lesson-title">{lesson.title}</h3>
                            
                            {lesson.description && (
                                <p className="lesson-description">{lesson.description}</p>
                            )}
                            
                            <div className="lesson-meta">
                                <span className="meta-item">Область: {lesson.area_name || 'Не указана'}</span>
                                <span className="meta-item">Уровень: {lesson.level_name}</span>
                            </div>
                            
                            <div className="lesson-actions">
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMoveLessonUp(index);
                                        }}
                                        className="btn-move"
                                        disabled={index === 0 || reordering}
                                        title="Переместить вверх"
                                        style={{
                                            opacity: index === 0 ? 0.4 : 1,
                                            cursor: index === 0 ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        <FiArrowUp />
                                    </button>
                                    <button 
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleMoveLessonDown(index);
                                        }}
                                        className="btn-move"
                                        disabled={index === lessons.length - 1 || reordering}
                                        title="Переместить вниз"
                                        style={{
                                            opacity: index === lessons.length - 1 ? 0.4 : 1,
                                            cursor: index === lessons.length - 1 ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        <FiArrowDown />
                                    </button>
                                </div>
                                <button 
                                    onClick={() => handleViewDetails(lesson)}
                                    className="btn-view"
                                    title="Просмотр"
                                >
                                    <FiEye />
                                    <span className="btn-text">Просмотр</span>
                                </button>
                                <button 
                                    onClick={() => navigate(`/admin/lessons-blocks/edit/${lesson.id}`)}
                                    className="btn-edit"
                                    title="Редактор"
                                >
                                    <FiEdit />
                                    <span className="btn-text">Редактор</span>
                                </button>
                                <button 
                                    onClick={() => handleDeleteLesson(lesson.id, lesson.title)}
                                    className="btn-delete"
                                    title="Удалить"
                                >
                                    <FiTrash2 />
                                    <span className="btn-text">Удалить</span>
                                </button>
                            </div>
                        </div>
                    ))
                )}
            </div>
        </div>
        </div>
    );
}

export default ViewLessonsPage;
